/**
 * StickySectionNav — Feb 2026 redesign.
 *
 * - Appears once the hero scrolls out of view, sits directly below the
 *   global site header (measured dynamically).
 * - Six section links + a yellow "Schedule" CTA pinned to the right.
 * - Active link gets a brand-blue color + an animated yellow dot
 *   indicator below it (sliding bar effect on hover too).
 * - Mobile: links become a horizontal-scrollable rail (snap to start)
 *   with the yellow Schedule button sticky-pinned to the right edge so
 *   the primary action is always reachable.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

const SECTIONS = [
  { id: 'how-camps-work', label: 'How camps work' },
  { id: 'included',       label: "What's included" },
  { id: 'mentors',        label: "Who they'll learn from" },
  { id: 'testimonials',   label: 'Testimonials' },
  { id: 'accreditation',  label: 'Accreditation' },
  { id: 'faq',            label: 'FAQ' },
];

export default function StickySectionNav() {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(null);
  const [topOffset, setTopOffset] = useState(0);
  const [railHasScrolled, setRailHasScrolled] = useState(false);
  const rafRef = useRef(0);
  const railRef = useRef(null);

  // Keep the active link visible inside the horizontal scroll rail on
  // mobile — otherwise the user has no idea which section they're in
  // if its pill is off-screen to the right.
  useEffect(() => {
    if (!active || !railRef.current) return;
    const link = railRef.current.querySelector(`[data-testid="sticky-nav-${active}"]`);
    if (link?.scrollIntoView) {
      link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [active]);

  // Watch the rail's own horizontal scroll so we can fade the
  // "more sections →" hint after the user actually touches it.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const onRailScroll = () => {
      if (!railHasScrolled && rail.scrollLeft > 6) setRailHasScrolled(true);
    };
    rail.addEventListener('scroll', onRailScroll, { passive: true });
    return () => rail.removeEventListener('scroll', onRailScroll);
  }, [railHasScrolled]);

  // Measure the global site header so we render directly below it.
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header[role="banner"]');
      if (header) {
        const rect = header.getBoundingClientRect();
        setTopOffset(Math.max(0, rect.bottom));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, []);

  // Toggle the bar based on hero scroll position. Scroll-based is more
  // reliable than an IntersectionObserver here — on mobile the hero
  // stacks taller and the IO threshold can stay stuck on `intersecting`
  // even when the user is well past the hero content.
  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById('summer-camps-hero');
      if (!hero) return;
      // Show once the hero is mostly behind us (bottom edge within
      // ~200px of the viewport top — generous so it appears as soon as
      // the user is engaged with content below).
      const bottom = hero.getBoundingClientRect().bottom;
      setVisible(bottom < 200);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Active-link tracking — closest section above the viewport top.
  // Schedule (`browse-camps`) is treated as a positional checkpoint so
  // the active state correctly hands off:
  //   ...Accreditation → Schedule (highlights yellow CTA) → FAQ
  // We can't just "override to schedule whenever it's above the probe"
  // because FAQ sits *below* schedule in DOM order — that broke when
  // the user scrolled into FAQ.
  useEffect(() => {
    // Track schedule + all SECTIONS pills together, in DOM order, so
    // the last-above-probe match correctly walks through Schedule on
    // the way to FAQ.
    const TRACKED = [
      ...SECTIONS.slice(0, 5).map((s) => s.id),  // up to Accreditation
      'browse-camps',                            // schedule grid
      'faq',                                     // FAQ accordions
    ];
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const probe = window.innerHeight * 0.3;
        let current = null;
        for (const id of TRACKED) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.top <= probe) current = id;
        }
        setActive(current);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    // Manually offset the scroll target so the eyebrow doesn't end up
    // hidden behind the global header + this sticky bar. (Browser
    // smooth-scrollIntoView ignores `scroll-margin-top` on JS-driven
    // jumps in most engines.)
    const header = document.querySelector('header[role="banner"]');
    const headerH = header ? header.getBoundingClientRect().height : 0;
    const navH = 56; // matches the h-12/h-14 sticky bar
    const targetTop =
      el.getBoundingClientRect().top + window.scrollY - headerH - navH - 8;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  };

  return (
    <div
      aria-hidden={!visible}
      data-testid="sticky-section-nav"
      style={{ top: `${topOffset}px` }}
      className={`fixed left-0 right-0 z-30 transition-transform duration-300 ${
        visible ? 'translate-y-0' : '-translate-y-full pointer-events-none'
      }`}
    >
      <div className="bg-white/90 backdrop-blur-md border-b border-rsc-card-border shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] font-poppins">
        <div className="container-app relative flex items-center h-12 sm:h-14">
          {/* Scroll rail — links live here. Pad right so the sticky
              Schedule pill never overlaps the last link on mobile. */}
          <nav
            ref={railRef}
            aria-label="Page sections"
            className="flex-1 -mx-2 pl-2 overflow-x-auto scrollbar-none scroll-smooth snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none' }}
          >
            <ul className="flex items-stretch gap-0.5 sm:gap-1 h-full">
              {SECTIONS.map((s) => {
                const isActive = active === s.id;
                return (
                  <li key={s.id} className="snap-start flex items-stretch">
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => handleClick(e, s.id)}
                      className="group relative inline-flex items-center px-2.5 sm:px-3.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap transition-colors focus:outline-none"
                      data-testid={`sticky-nav-${s.id}`}
                    >
                      <span
                        className={`transition-colors ${
                          isActive
                            ? 'text-rsc-brand-blue'
                            : 'text-rsc-charcoal-muted group-hover:text-rsc-brand-blue'
                        }`}
                      >
                        {s.label}
                      </span>
                      {/* Animated indicator — yellow bar that grows under
                          the link. Sits at the bottom of the nav bar. */}
                      <span
                        aria-hidden
                        className={`pointer-events-none absolute left-2 right-2 sm:left-3 sm:right-3 bottom-0 h-[3px] rounded-t-full bg-rsc-yellow origin-center transition-transform duration-300 ${
                          isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50'
                        }`}
                      />
                    </a>
                  </li>
                );
              })}
              {/* End-of-rail spacer — guarantees the last pill (FAQ) can
                  scroll fully out from underneath the right-pinned
                  Schedule CTA on mobile. `padding-right` on the nav
                  isn't reliably counted toward flex overflow scroll
                  width in all browsers, so we use a real element. */}
              <li aria-hidden className="shrink-0 w-36 sm:w-40" />
            </ul>
          </nav>

          {/* Right-pinned Schedule CTA. Sticky-positioned so it always
              sits over the right edge even while the link rail scrolls
              underneath. The gradient fade on its left side also
              doubles as a "more sections →" affordance on mobile — it
              softly hides any link pills sliding under it. */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pl-6 pr-1 bg-gradient-to-l from-white via-white/95 to-white/0">
            {/* Mobile-only chevron nudge — quiet hint that the rail
                scrolls horizontally. Fades out the moment the user
                interacts with the rail. */}
            <span
              aria-hidden
              className={`md:hidden absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-rsc-slate-soft border border-rsc-card-border shadow-sm transition-opacity duration-300 ${
                railHasScrolled ? 'opacity-0' : 'opacity-100 animate-pulse'
              }`}
            >
              <ChevronRight className="w-3 h-3 text-rsc-brand-blue" />
            </span>
            <a
              href="#browse-camps"
              onClick={(e) => handleClick(e, 'browse-camps')}
              className={`relative inline-flex items-center gap-1.5 px-3.5 sm:px-4 h-8 sm:h-9 rounded-full bg-rsc-yellow text-rsc-brand-blue text-[13px] sm:text-sm font-extrabold hover:bg-rsc-yellow/90 shadow-md shadow-amber-200/60 hover:shadow-lg active:scale-95 transition-all whitespace-nowrap ${
                active === 'browse-camps' ? 'ring-2 ring-rsc-brand-blue ring-offset-2 ring-offset-white' : ''
              }`}
              data-testid="sticky-nav-schedule"
              aria-current={active === 'browse-camps' ? 'true' : undefined}
            >
              Schedule
              <span
                aria-hidden
                className={`inline-block w-1.5 h-1.5 rounded-full transition-colors ${
                  active === 'browse-camps' ? 'bg-rsc-brand-blue animate-pulse' : 'bg-rsc-brand-blue/40'
                }`}
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
